import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API_BASE, getStoredToken, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft, FileText, Download, Mail, CheckCircle2, XCircle,
  Loader2, Clock,
} from "lucide-react";

const STATUS_PILL = {
  Pending: "bg-amber-500 text-slate-900 border-amber-700",
  Approved: "bg-emerald-700 text-white border-emerald-900",
  Rejected: "bg-rose-700 text-white border-rose-900",
};

export default function ToolRequestDetail() {
  const { id } = useParams();
  const { user, isPrivileged } = useAuth();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [decisionNotes, setDecisionNotes] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tool-requests/${id}`);
      setDoc(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load request");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const tok = getStoredToken();
      const res = await fetch(`${API_BASE}/tool-requests/${id}/letter.pdf`, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tool-request-${doc?.Request_Number || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setBusy(false);
    }
  };

  const openMailDraft = () => {
    if (!doc) return;
    const to = (doc.Supervisor_Email || "").trim();
    if (!to) {
      toast.error("No supervisor email on this request. Edit and add one first.");
      return;
    }
    const subj = `Tool Request ${doc.Request_Number} — please review`;
    const tools = (doc.Tool_Snapshot || []).map((t) => ` - ${t.Tool_Name} (Cat ID: ${t.Cat_ID})`).join("\n");
    const lines = [
      `Hi,`,
      ``,
      `Please review and approve tool request ${doc.Request_Number}.`,
      ``,
      `Project / Station: ${doc.Project_Station}`,
      `Requestor: ${doc.Requestor_Name} (${doc.Requestor_Email})`,
      `Created: ${(doc.Created_At || "").slice(0, 10)}`,
      ``,
      `Tools requested:`,
      tools,
      ``,
      doc.Requestor_Notes ? `Notes: ${doc.Requestor_Notes}` : "",
      ``,
      `The signed approval letter (PDF) is available in the app — open the link below to download:`,
      window.location.href,
      ``,
      `Thanks,`,
      `${doc.Requestor_Name}`,
    ].filter(Boolean).join("\n");
    const url = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(lines)}`;
    window.location.href = url;
  };

  const decide = async (decision) => {
    if (!doc) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tool-requests/${id}/decision`, {
        decision,
        notes: decisionNotes.trim(),
      });
      setDoc(data);
      toast.success(`${decision}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="px-4 py-8 text-center text-slate-500 flex items-center justify-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (!doc) {
    return <div className="px-4 py-8 text-center text-slate-500">Not found.</div>;
  }

  const tools = doc.Tool_Snapshot || [];
  const created = doc.Created_At ? new Date(doc.Created_At) : null;

  return (
    <div className="px-4 sm:px-6 py-5 max-w-4xl mx-auto pb-24" data-testid="tool-request-detail">
      <button
        type="button"
        onClick={() => nav(-1)}
        className="text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-3"
        data-testid="tool-request-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>

      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700 mb-0.5">
            Tool Request
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-slate-900 uppercase tracking-tight leading-none" data-testid="tool-request-number">
            {doc.Request_Number}
          </h1>
        </div>
        <span
          data-testid="tool-request-status"
          className={`inline-block px-3 py-1 text-xs font-bold uppercase tracking-wider border-2 rounded-sm ${STATUS_PILL[doc.Status] || ""}`}
        >
          {doc.Status}
        </span>
      </div>

      <div className="rg-card p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <Pair label="Supervisor Name" value={doc.Supervisor_Name || "—"} />
        <Pair label="Requestor" value={`${doc.Requestor_Name} · ${doc.Requestor_Email}`} />
        {doc.Project_Station && <Pair label="Project / Station" value={doc.Project_Station} />}
        {doc.Yard_Storage && <Pair label="Yard Storage" value={doc.Yard_Storage} />}
        <Pair label="Created" value={created ? created.toLocaleString() : "—"} />
        <Pair label="Supervisor email" value={doc.Supervisor_Email || "—"} />
        {doc.Requestor_Notes && (
          <div className="sm:col-span-2">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-0.5">
              Requestor notes
            </div>
            <div className="text-slate-700 whitespace-pre-wrap">{doc.Requestor_Notes}</div>
          </div>
        )}
      </div>

      {/* Letter preview */}
      <div className="rg-card p-5 sm:p-7 mb-4 bg-white border-2 border-slate-300" data-testid="tool-request-letter-preview">
        <div className="text-xs text-right text-slate-600 mb-2">
          {created ? created.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : ""}
        </div>
        <h2 className="font-display text-xl text-slate-900 uppercase mb-1" data-testid="letter-brand">
          {(doc.Supervisor_Name || doc.location || "ROUGEE")} Material Tracker
        </h2>
        <p className="text-xs text-slate-500 mb-4">
          Tool Request {doc.Request_Number} · Location: {doc.location || "—"}
          {doc.Supervisor_Name && (
            <>
              <br />Supervisor Name: <strong>{doc.Supervisor_Name}</strong>
            </>
          )}
        </p>
        <p className="text-sm text-slate-700 mb-2">To the Approving Supervisor:</p>
        <p className="text-sm text-slate-700 mb-4">
          This letter formally requests approval to issue the following ComEd-approved
          tools{doc.Project_Station ? <> for use on project <strong>{doc.Project_Station}</strong></> : null}.
          The request is submitted by <strong>{doc.Requestor_Name}</strong> (
          {doc.Requestor_Email}) on the date listed above.
        </p>
        <table className="w-full text-sm border-2 border-slate-300 mb-4">
          <thead>
            <tr className="bg-slate-900 text-white">
              <th className="py-2 px-2 text-left w-8">#</th>
              <th className="py-2 px-2 text-left">Tool</th>
              <th className="py-2 px-2 text-left w-32">Cat ID</th>
              <th className="py-2 px-2 text-left w-32">Category</th>
            </tr>
          </thead>
          <tbody>
            {tools.map((t, i) => (
              <tr key={t.id} className={i % 2 ? "bg-slate-50" : ""}>
                <td className="py-1.5 px-2">{i + 1}</td>
                <td className="py-1.5 px-2 font-bold text-slate-900">{t.Tool_Name}</td>
                <td className="py-1.5 px-2 font-mono">{t.Cat_ID}</td>
                <td className="py-1.5 px-2 text-slate-700">{t.Category || "General"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {doc.Requestor_Notes && (
          <>
            <p className="text-sm font-bold text-slate-900 mb-1">Requestor notes</p>
            <p className="text-sm text-slate-700 whitespace-pre-wrap mb-4">{doc.Requestor_Notes}</p>
          </>
        )}
        <p className="text-sm text-slate-700 mb-6">
          Please review the list above and indicate approval by signing and dating the
          spaces provided. Approved tools will be checked out from the yard
          and tracked through their return.
        </p>
        <div className="grid grid-cols-2 gap-6 mt-8">
          <div>
            <div className="border-b-2 border-slate-900 h-8" />
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">Supervisor signature</div>
          </div>
          <div>
            <div className="border-b-2 border-slate-900 h-8" />
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">Date</div>
          </div>
          <div>
            <div className="border-b-2 border-slate-900 h-8" />
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">Printed name</div>
          </div>
          <div>
            <div className="border-b-2 border-slate-900 h-8" />
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">Title</div>
          </div>
        </div>
        {doc.Status === "Approved" && (
          <p className="text-sm font-bold text-emerald-800 mt-5">
            Approved by {doc.Approved_By} on {(doc.Approved_At || "").slice(0, 10)}
          </p>
        )}
        {doc.Status === "Rejected" && (
          <p className="text-sm font-bold text-rose-800 mt-5">
            Rejected by {doc.Approved_By} on {(doc.Approved_At || "").slice(0, 10)}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
        <button
          type="button"
          onClick={downloadPdf}
          disabled={busy}
          data-testid="tool-request-download-pdf"
          className="rg-tap bg-slate-900 hover:bg-slate-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 px-4 min-h-[52px] disabled:opacity-50 border-2 border-slate-700"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Download PDF
        </button>
        <button
          type="button"
          onClick={openMailDraft}
          data-testid="tool-request-mailto"
          className="rg-tap bg-emerald-700 hover:bg-emerald-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 px-4 min-h-[52px] border-2 border-emerald-900"
        >
          <Mail className="h-4 w-4" />
          Open in mail app
        </button>
      </div>

      {/* Supervisor decision panel — admin/supervisor only and only while Pending */}
      {isPrivileged && doc.Status === "Pending" && (
        <div className="rg-card p-4 border-2 border-amber-300 bg-amber-50" data-testid="tool-request-decision-panel">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-900 mb-2 flex items-center gap-1">
            <Clock className="h-3 w-3" /> Awaiting your decision
          </div>
          <textarea
            value={decisionNotes}
            onChange={(e) => setDecisionNotes(e.target.value)}
            rows={2}
            placeholder="Approval / rejection notes (optional)"
            data-testid="tool-request-decision-notes"
            maxLength={2000}
            className="w-full border-2 border-slate-300 rounded-sm px-3 py-2 text-sm bg-white focus:border-amber-700 outline-none mb-2"
          />
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => decide("Approved")}
              disabled={busy}
              data-testid="tool-request-approve-btn"
              className="rg-tap bg-emerald-700 hover:bg-emerald-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-1.5 px-4 min-h-[48px] border-2 border-emerald-900 disabled:opacity-50"
            >
              <CheckCircle2 className="h-4 w-4" /> Approve
            </button>
            <button
              type="button"
              onClick={() => decide("Rejected")}
              disabled={busy}
              data-testid="tool-request-reject-btn"
              className="rg-tap bg-rose-700 hover:bg-rose-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-1.5 px-4 min-h-[48px] border-2 border-rose-900 disabled:opacity-50"
            >
              <XCircle className="h-4 w-4" /> Reject
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Pair({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-0.5">
        {label}
      </div>
      <div className="text-sm font-bold text-slate-900 truncate" title={value}>{value || "—"}</div>
    </div>
  );
}
